import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useExperiences } from '../context/ExperiencesContext';
import { fetchOccurrencesForExperience } from '../services/experiencesFirestore';
import ExperienceMiniCard from './ExperienceMiniCard';
import ExperienceRating from './ExperienceRating';
import {
  normalizeExperience,
  getExperienceShareUrl,
  getExperienceCanonicalUrl,
  getRealityCheckPreviewText,
  shouldShowAboutSection
} from '../utils/normalizeExperience';
import {
  renderQuickFacts,
  renderCtaLabel,
  renderCtaMicrocopy,
  renderPriceDisplay,
  renderPublishPlatformLine,
  resolvePrimaryCtaAction,
  renderExploreSectionOrder,
  renderAvailabilityStatus,
  renderVerifiedLine,
  getReferenceFields,
  hasAccessibilitySection,
  hasCreatorSection,
  getCompatibleSurfaces
} from '../utils/experienceRenderers';
import {
  getNearbyExperiences,
  getExperiencesByCreator,
  getSimilarExperiences,
  getCreatorOtherExperiences
} from '../utils/experienceProximity';
import AccessDeviceIcons from './AccessDeviceIcons';
import { getAccessDevices } from '../utils/experienceDeviceAccess';
import { labelsFromSlugs, LOCOMOTION_LABELS, CONTENT_NOTE_LABELS, ACCESSIBILITY_LABELS, isPhysicalPrimaryFormat, getComePreparedEmoji } from '../utils/experienceOntology';

const CURRENT_DEVICE = 'browser';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(10, 0, 20, 0.92);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;

  @media (min-width: 640px) {
    align-items: center;
    padding: 12px;
  }
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, rgba(20, 5, 35, 0.98) 0%, rgba(15, 10, 40, 0.98) 100%);
  backdrop-filter: blur(20px);
  border-radius: 24px 24px 0 0;
  border: 1px solid rgba(255, 0, 170, 0.3);
  max-width: 100%;
  width: 100%;
  position: relative;
  flex-shrink: 0;
  max-height: 96dvh;
  max-height: 96vh;
  box-shadow:
    0 0 40px rgba(255, 0, 170, 0.2),
    0 0 80px rgba(0, 245, 255, 0.1),
    0 25px 50px rgba(0, 0, 0, 0.5);

  @media (min-width: 768px) {
    max-width: 1000px;
    border-radius: 24px;
    max-height: calc(100dvh - 24px);
    max-height: calc(100vh - 24px);
  }
`;

const ModalBody = styled.div`
  max-height: inherit;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  border-radius: inherit;
`;

const PreviewInlineShell = styled.div`
  width: 100%;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 0, 170, 0.3);
  background: linear-gradient(135deg, rgba(20, 5, 35, 0.98) 0%, rgba(15, 10, 40, 0.98) 100%);
  box-shadow:
    0 0 40px rgba(255, 0, 170, 0.15),
    0 0 80px rgba(0, 245, 255, 0.08);
`;

const PreviewInlinePanel = styled.div`
  width: 100%;
  position: relative;
  max-height: none;
`;

const CloseButton = styled.button`
  position: fixed;
  top: max(12px, env(safe-area-inset-top, 12px));
  right: max(12px, env(safe-area-inset-right, 12px));
  z-index: 1001;
  background: rgba(10, 0, 20, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.45);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  line-height: 1;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.55);

  &:hover {
    background: rgba(255, 0, 170, 0.65);
    border-color: rgba(255, 0, 170, 0.75);
  }
`;

const Hero = styled.div`
  width: 100%;
  height: 220px;
  flex-shrink: 0;
  background: url(${(p) => p.$src}) center/cover;
  background-color: rgba(255, 255, 255, 0.06);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(transparent, rgba(20, 5, 35, 0.98));
  }
`;

const EditorialBadge = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
  background: rgba(255, 0, 170, 0.9);
  color: #fff;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const PlayOverlay = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  border: none;
  background: rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='black' d='M8 5v14l11-7z'/%3E%3C/svg%3E") center / 28px no-repeat;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='black' d='M8 5v14l11-7z'/%3E%3C/svg%3E") center / 28px no-repeat;
  }
`;

const Content = styled.div`
  padding: 20px 22px 32px;
`;

const BrandMark = styled.p`
  margin: 0 0 8px;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  text-align: left;
  opacity: 0.82;
  background: linear-gradient(135deg, #ff00aa, #00f5ff, #ffee00);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: modalBrandShimmer 6s ease infinite;

  @keyframes modalBrandShimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const Title = styled.h2`
  font-size: 1.55rem;
  font-weight: 700;
  margin: 0 0 10px;
  color: white;
  line-height: 1.2;
`;

const CreatorRow = styled.button`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 14px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const FormatPill = styled.span`
  display: inline-block;
  background: rgba(0, 245, 255, 0.15);
  border: 1px solid rgba(0, 245, 255, 0.35);
  color: #fff;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 20px;
`;

const RatingsSection = styled.section`
  margin-top: 8px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const RatingsHeading = styled.h2`
  margin: 0 0 12px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;

const SectionLabel = styled.h3`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 10px;
`;

const PreviewText = styled.p`
  font-size: 1.08rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.92);
  margin: 0 0 22px;
`;

const QuickFactsList = styled.ul`
  list-style: none;
  margin: 0 0 22px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 20px;
  }
`;

const QuickFactItem = styled.li`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.88);
  display: flex;
  gap: 8px;
  align-items: flex-start;

  &::before {
    content: '✓';
    color: #00f5ff;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const BestForEffortRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
`;

const BestForChipRow = styled(ChipRow)`
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
`;

const Chip = styled.span`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.9);
`;

const EffortText = styled.p`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;

  @media (min-width: 768px) {
    flex-shrink: 0;
    max-width: 42%;
    text-align: right;
    line-height: 1.4;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.88);
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const PrimaryCTA = styled(motion.button)`
  width: 100%;
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
  padding: 14px 18px;
  border-radius: 14px;
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  box-shadow: 0 0 20px rgba(255, 0, 170, 0.35);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CtaMicrocopy = styled.p`
  text-align: center;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 10px 0 16px;
`;

const HeaderEssentials = styled.div`
  margin: 0 0 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const HeaderPriceLine = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.45;
`;

const HeaderPlatformLine = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.45;
`;

const HeaderDeviceIcons = styled(AccessDeviceIcons)`
  margin-top: 2px;
`;

const SecondaryRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SecondaryButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  flex: 1;
  min-width: 90px;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SaveButton = styled(SecondaryButton)`
  background: ${(p) => (p.$filled ? 'rgba(255, 0, 170, 0.25)' : 'rgba(255, 255, 255, 0.08)')};
  border-color: ${(p) => (p.$filled ? '#ff00aa' : 'rgba(255, 255, 255, 0.18)')};
  color: ${(p) => (p.$filled ? '#ff00aa' : '#fff')};
`;

const VerificationLine = styled.p`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 24px;
`;

const ExploreSection = styled(motion.section)`
  margin-bottom: ${(p) => (p.$compact ? '12px' : '22px')};
  padding-top: ${(p) => (p.$compact ? '2px' : '6px')};
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const ExploreHeading = styled.h3`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 ${(p) => (p.$compact ? '6px' : '12px')};
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GalleryScroll = styled(HorizontalScroll)`
  scroll-snap-type: x mandatory;
  gap: 16px;
  margin: 0 -22px;
  padding: 0 22px 8px;
`;

const GalleryItem = styled.div`
  flex: 0 0 100%;
  min-width: 100%;
  height: 280px;
  border-radius: 14px;
  overflow: hidden;
  scroll-snap-align: start;
  background-color: rgba(255, 255, 255, 0.06);

  @media (min-width: 768px) {
    height: 380px;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BodyText = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  white-space: pre-wrap;
`;

const VenueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$compact ? '4px' : '10px')};
`;

const VenueInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 28px;
  }
`;

const VenueLine = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
`;

const VenueTicketRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (min-width: 768px) {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: baseline;
    gap: 20px;
    flex-wrap: wrap;
  }
`;

const VenueTicketNote = styled(VenueLine)`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.65);

  @media (min-width: 768px) {
    flex: 1;
    min-width: 200px;
  }
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #00f5ff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const OccurrenceCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
`;

const OccurrenceMeta = styled.div`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.45;
`;

const OccurrenceBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.1);
  text-transform: capitalize;
`;

const DiscoverSection = styled.section`
  margin-bottom: ${(p) => (p.$compact ? '14px' : '22px')};
`;

const DiscoverTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: #fff;
`;

const ReferenceToggle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 8px;
`;

const ReferencePanel = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 24px;
`;

const ReferenceMainGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 14px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px 24px;
    margin-bottom: 14px;
  }
`;

const ReferenceGroup = styled.div`
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReferenceGridGroup = styled(ReferenceGroup)`
  margin-bottom: 0;
`;

const ReferenceGroupTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 8px;
`;

const ReferenceRow = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 4px;
  line-height: 1.4;
`;

const InnerOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
`;

const InnerModal = styled(motion.div)`
  background: linear-gradient(135deg, rgba(20, 5, 35, 0.99) 0%, rgba(15, 10, 40, 0.99) 100%);
  border: 1px solid rgba(255, 0, 170, 0.35);
  border-radius: 20px;
  padding: 24px;
  max-width: 360px;
  width: 100%;
  position: relative;
`;

const InnerClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
`;

const InnerTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 1.15rem;
  color: #fff;
`;

const InnerSubtitle = styled.p`
  margin: 0 0 16px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.65);
`;

const QRBox = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0;
  padding: 16px;
  background: #fff;
  border-radius: 12px;

  svg {
    display: block;
    width: 200px;
    height: 200px;
  }
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #000;
  border-radius: 12px;
  overflow: hidden;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

function formatOccurrenceDate(date, timezone) {
  if (!date) return '';
  try {
    const opts = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    const formatted = new Intl.DateTimeFormat(undefined, opts).format(date);
    return timezone ? `${formatted} (${timezone})` : formatted;
  } catch {
    return String(date);
  }
}

function SimpleModal({
  experience,
  onClose,
  onLaunch,
  onExperienceClick,
  showSaveButton,
  isSaved,
  onSaveClick,
  previewMode = false
}) {
  const { experiences: allExperiences } = useExperiences();
  const ctaRef = useRef(null);
  const [immersionOpen, setImmersionOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [venueExpanded, setVenueExpanded] = useState(false);
  const [accessibilityExpanded, setAccessibilityExpanded] = useState(false);
  const [creatorBioExpanded, setCreatorBioExpanded] = useState(false);
  const [occurrencesExpanded, setOccurrencesExpanded] = useState(false);
  const [occurrences, setOccurrences] = useState([]);
  const [tourExpanded, setTourExpanded] = useState(false);

  const exp = useMemo(() => normalizeExperience(experience), [experience]);

  useEffect(() => {
    if (previewMode) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;
    if (!exp?.id) return;
    if (exp.availabilityType !== 'scheduled' && exp.availabilityType !== 'limited-run') return;
    let cancelled = false;
    fetchOccurrencesForExperience(exp.id, 20).then((list) => {
      if (!cancelled) setOccurrences(list);
    });
    return () => { cancelled = true; };
  }, [previewMode, exp?.id, exp?.availabilityType]);

  const quickFacts = useMemo(() => renderQuickFacts(exp), [exp]);
  const ctaLabel = useMemo(() => renderCtaLabel(exp), [exp]);
  const ctaMicrocopy = useMemo(() => renderCtaMicrocopy(exp), [exp]);
  const ctaAction = useMemo(() => resolvePrimaryCtaAction(exp), [exp]);
  const priceDisplay = useMemo(() => renderPriceDisplay(exp), [exp]);
  const publishPlatformLine = useMemo(() => renderPublishPlatformLine(exp), [exp]);
  const availability = useMemo(() => renderAvailabilityStatus(exp), [exp]);
  const verifiedLine = useMemo(() => renderVerifiedLine(exp), [exp]);
  const exploreOrder = useMemo(() => renderExploreSectionOrder(exp), [exp]);
  const referenceFields = useMemo(() => getReferenceFields(exp), [exp]);
  const previewText = useMemo(() => getRealityCheckPreviewText(exp), [exp]);
  const showAbout = useMemo(() => shouldShowAboutSection(exp), [exp]);
  const shareUrl = useMemo(() => getExperienceShareUrl(exp?.id), [exp?.id]);
  const canonicalUrl = useMemo(() => getExperienceCanonicalUrl(exp), [exp]);
  const compatibleSurfaces = useMemo(() => getCompatibleSurfaces(exp), [exp]);
  const accessDevices = useMemo(() => getAccessDevices(exp), [exp]);
  const showHeaderEssentials = priceDisplay || publishPlatformLine || accessDevices.length;

  const supportedDevices = experience?.supportedDevices ?? ['browser'];
  const isCompatible = supportedDevices.includes(CURRENT_DEVICE);
  const hasLaunchUrl = !!exp?.launchUrl;
  const canPrimaryCta = !!ctaAction?.url;

  const moreFromCreator = useMemo(
    () => (previewMode ? [] : getExperiencesByCreator(exp?.creatorId, allExperiences, exp, 3)),
    [previewMode, exp?.creatorId, exp, allExperiences]
  );
  const similar = useMemo(
    () => (previewMode ? [] : getSimilarExperiences(exp, allExperiences, 6)),
    [previewMode, exp, allExperiences]
  );
  const nearby = useMemo(
    () => (previewMode ? [] : getNearbyExperiences(exp, allExperiences, 6)),
    [previewMode, exp, allExperiences]
  );
  const creatorOthers = useMemo(
    () => (previewMode ? [] : getCreatorOtherExperiences(exp?.creatorId, allExperiences, exp, 3)),
    [previewMode, exp?.creatorId, exp, allExperiences]
  );

  const handleShare = useCallback(async () => {
    const shareData = {
      title: exp?.title,
      text: exp?.officialDescription || exp?.realityCheckPreview || '',
      url: shareUrl
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== 'AbortError') navigator.clipboard?.writeText(shareUrl);
      }
    } else {
      navigator.clipboard?.writeText(shareUrl);
    }
  }, [exp, shareUrl]);

  const handlePrimaryCta = useCallback(() => {
    if (!ctaAction?.url) return;
    if (ctaAction.type === 'maps') {
      window.open(ctaAction.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isCompatible || exp?.isLBE || exp?.isPhysicalVenue) {
      onLaunch?.(experience);
    } else {
      window.open(ctaAction.url, '_blank', 'noopener,noreferrer');
    }
  }, [ctaAction, isCompatible, onLaunch, experience, exp?.isLBE, exp?.isPhysicalVenue]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard?.writeText(canonicalUrl);
  }, [canonicalUrl]);

  const formatPillText = [exp?.formatBadge, exp?.formatDescriptor].filter(Boolean).join(' · ');

  const renderGallery = () => {
    if (!exp?.galleryUrls?.length) return null;
    return (
      <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <ExploreHeading>Gallery</ExploreHeading>
        <GalleryScroll>
          {exp.galleryUrls.map((url, i) => (
            <GalleryItem key={url + i}>
              <GalleryImage src={url} alt={`Gallery image ${i + 1}`} loading="lazy" />
            </GalleryItem>
          ))}
        </GalleryScroll>
      </ExploreSection>
    );
  };

  const renderAbout = () => {
    if (!showAbout || !exp?.officialDescription) return null;
    return (
      <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <ExploreHeading>About</ExploreHeading>
        <BodyText>{exp.officialDescription}</BodyText>
      </ExploreSection>
    );
  };

  const renderAvailability = () => {
    const type = exp?.availabilityType;

    const ticketAvailabilityExtras =
      (exp?.priceType === 'ticket' || exp?.priceType === 'paid' || exp?.availabilityType === 'scheduled') ? (
        <VenueLine style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
          Availability is not guaranteed. Book early for best results.
        </VenueLine>
      ) : null;

    if (type === 'scheduled' || type === 'limited-run') {
      if (occurrences.length === 0) return null;
      const visible = occurrencesExpanded ? occurrences : occurrences.slice(0, 3);
      return (
        <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <ExploreHeading>Availability</ExploreHeading>
          {ticketAvailabilityExtras}
          {visible.map((occ) => (
            <OccurrenceCard key={occ.id}>
              <OccurrenceMeta>
                <strong>{formatOccurrenceDate(occ.startDatetime, occ.timezone)}</strong>
                {(occ.venueName || occ.venueCity) && (
                  <div>{[occ.venueName, occ.venueCity].filter(Boolean).join(' · ')}</div>
                )}
                {occ.priceLabel && <div>{occ.priceLabel}</div>}
              </OccurrenceMeta>
              {occ.status && <OccurrenceBadge>{occ.status.replace('-', ' ')}</OccurrenceBadge>}
              {occ.ticketUrl && (
                <TextButton
                  type="button"
                  style={{ marginTop: 10 }}
                  onClick={() => window.open(occ.ticketUrl, '_blank', 'noopener,noreferrer')}
                >
                  Get tickets
                </TextButton>
              )}
            </OccurrenceCard>
          ))}
          {occurrences.length > 3 && (
            <TextButton type="button" onClick={() => setOccurrencesExpanded((v) => !v)}>
              {occurrencesExpanded ? 'Show fewer dates' : `Show all ${occurrences.length} dates`}
            </TextButton>
          )}
        </ExploreSection>
      );
    }

    if (type === 'touring' && exp.tourStops?.length) {
      const stops = tourExpanded ? exp.tourStops : exp.tourStops.slice(0, 3);
      return (
        <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <ExploreHeading>Availability</ExploreHeading>
          {ticketAvailabilityExtras}
          {stops.map((stop, i) => (
            <OccurrenceCard key={stop.id || i}>
              <OccurrenceMeta>
                <strong>{stop.city}</strong>
                {stop.dateRange && <div>{stop.dateRange}</div>}
                {stop.status && <OccurrenceBadge>{stop.status}</OccurrenceBadge>}
              </OccurrenceMeta>
              {stop.ticketUrl && (
                <TextButton type="button" style={{ marginTop: 8 }} onClick={() => window.open(stop.ticketUrl, '_blank')}>
                  Tickets
                </TextButton>
              )}
            </OccurrenceCard>
          ))}
          {exp.tourStops.length > 3 && (
            <TextButton type="button" onClick={() => setTourExpanded((v) => !v)}>
              {tourExpanded ? 'Show fewer stops' : `Show all ${exp.tourStops.length} stops`}
            </TextButton>
          )}
        </ExploreSection>
      );
    }

    if (type === 'permanent' && exp.isPhysicalVenue) {
      const hasVenue = exp.venueName || exp.venueAddress || exp.venueCity;
      if (!hasVenue && !exp.comePrepared?.length) return null;
      const mapsUrl =
        exp.venueLat != null && exp.venueLng != null
          ? `https://www.google.com/maps/search/?api=1&query=${exp.venueLat},${exp.venueLng}`
          : null;
      const detailLines = [exp.venueName, exp.venueAddress, exp.venueCity, exp.venueHours].filter(Boolean);
      const hasExpandable =
        exp.venueParking || exp.venueTransit || exp.venueAccessibilityEntrance;
      const compact = detailLines.length <= 1 && !mapsUrl && !exp.comePrepared?.length && !hasExpandable;
      return (
        <ExploreSection $compact={compact} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <ExploreHeading $compact={compact}>Availability</ExploreHeading>
          <VenueBlock $compact={compact}>
            {exp.availabilityStatusLabel && (
              <VenueLine><strong>{exp.availabilityStatusLabel}</strong></VenueLine>
            )}
            {exp.venueName && <VenueLine><strong>{exp.venueName}</strong></VenueLine>}
            <VenueInfoGrid>
              {(exp.venueAddress || exp.venueCity) && (
                <VenueLine>{[exp.venueAddress, exp.venueCity].filter(Boolean).join(', ')}</VenueLine>
              )}
              {exp.venueHours && <VenueLine>{exp.venueHours}</VenueLine>}
              {(exp?.priceType === 'ticket' || exp?.priceType === 'paid') && (
                <VenueTicketNote>
                  Availability is not guaranteed. Book early for best results.
                </VenueTicketNote>
              )}
            </VenueInfoGrid>
            {mapsUrl && (
              <TextButton type="button" onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}>
                Get Directions
              </TextButton>
            )}
            {venueExpanded && (
              <>
                {exp.venueParking && <VenueLine>Parking: {exp.venueParking}</VenueLine>}
                {exp.venueTransit && <VenueLine>Transit: {exp.venueTransit}</VenueLine>}
                {exp.venueAccessibilityEntrance && (
                  <VenueLine>Entrance: {exp.venueAccessibilityEntrance}</VenueLine>
                )}
              </>
            )}
            {(exp.venueParking || exp.venueTransit || exp.venueAccessibilityEntrance) && (
              <TextButton type="button" onClick={() => setVenueExpanded((v) => !v)}>
                {venueExpanded ? 'Show less' : 'Parking, transit & access'}
              </TextButton>
            )}
            {exp.comePrepared?.length > 0 && (
              <ChipRow style={{ marginTop: 8 }}>
                {exp.comePrepared.map((tip) => (
                  <Chip key={tip}>
                    <span aria-hidden="true">{getComePreparedEmoji(tip)}</span> {tip}
                  </Chip>
                ))}
              </ChipRow>
            )}
          </VenueBlock>
        </ExploreSection>
      );
    }

    if (type === 'persistent') {
      return (
        <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <ExploreHeading>Availability</ExploreHeading>
          {ticketAvailabilityExtras}
          {hasLaunchUrl && (
            <PrimaryCTA type="button" onClick={handlePrimaryCta} style={{ marginBottom: 12 }}>
              Join Now
            </PrimaryCTA>
          )}
          {occurrences.length > 0 && (
            <VenueLine style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)' }}>
              {occurrences.length} upcoming event{occurrences.length !== 1 ? 's' : ''} this week
            </VenueLine>
          )}
        </ExploreSection>
      );
    }

    return null;
  };

  const renderAccessibility = () => {
    if (!hasAccessibilitySection(exp)) return null;
    const isPhysical = isPhysicalPrimaryFormat(exp.primaryFormat);
    const locLabels = isPhysical ? [] : labelsFromSlugs(exp.locomotionTypes, LOCOMOTION_LABELS);
    const noteLabels = labelsFromSlugs(exp.contentNotes, CONTENT_NOTE_LABELS);
    const a11yLabels = labelsFromSlugs(exp.accessibilityFeatures, ACCESSIBILITY_LABELS);
    const chipCount = locLabels.length + noteLabels.length + a11yLabels.length;
    const compact = chipCount === 0 && !exp.physicalRequirements?.length;

    return (
      <ExploreSection $compact={compact} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <ExploreHeading $compact={compact}>Accessibility</ExploreHeading>
        {(locLabels.length > 0 || noteLabels.length > 0 || a11yLabels.length > 0) && (
          <ChipRow style={{ marginTop: 10 }}>
            {[...locLabels, ...noteLabels, ...a11yLabels].map((label) => (
              <Chip key={label}>{label}</Chip>
            ))}
          </ChipRow>
        )}
        {exp.physicalRequirements?.length > 0 && (
          <>
            {accessibilityExpanded && (
              <ChipRow>
                {exp.physicalRequirements.map((req) => (
                  <Chip key={req}>{req}</Chip>
                ))}
              </ChipRow>
            )}
            <TextButton type="button" onClick={() => setAccessibilityExpanded((v) => !v)}>
              {accessibilityExpanded ? 'Hide physical requirements' : 'Physical requirements'}
            </TextButton>
          </>
        )}
      </ExploreSection>
    );
  };

  const renderCreator = () => {
    if (!hasCreatorSection(exp)) return null;
    return (
      <ExploreSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <ExploreHeading>Creator</ExploreHeading>
        <VenueLine>
          <strong>{exp.creatorName}</strong>
          {exp.creatorVerified && ' ✓'}
        </VenueLine>
        {exp.creatorBio && (
          <>
            <BodyText style={{ marginTop: 8, fontSize: '0.88rem' }}>
              {exp.creatorBio.length > 160 && !creatorBioExpanded
                ? `${exp.creatorBio.slice(0, 160)}…`
                : exp.creatorBio}
            </BodyText>
            {exp.creatorBio.length > 160 && (
              <TextButton type="button" onClick={() => setCreatorBioExpanded((v) => !v)}>
                {creatorBioExpanded ? 'Show less' : 'Read more'}
              </TextButton>
            )}
          </>
        )}
        {!previewMode && creatorOthers.length > 0 && (
          <HorizontalScroll style={{ marginTop: 14 }}>
            {creatorOthers.map((item) => (
              <ExperienceMiniCard
                key={item.id}
                experience={item}
                onClick={onExperienceClick}
              />
            ))}
          </HorizontalScroll>
        )}
        {!previewMode && (
          <TextButton type="button" style={{ marginTop: 10 }}>
            View all from {exp.creatorName}
          </TextButton>
        )}
      </ExploreSection>
    );
  };

  const exploreSections = {
    gallery: renderGallery,
    about: renderAbout,
    availability: renderAvailability,
    accessibility: renderAccessibility,
    creator: renderCreator
  };

  const hasReferenceContent =
    referenceFields.experience.length +
    referenceFields.access.length +
    referenceFields.audience.length +
    referenceFields.time.length > 0;

  if (!experience || !exp) return null;

  const modalInner = (
    <ModalBody>
      {/* INTEREST */}
      <Hero $src={exp.heroImageUrl}>
        {exp.editorialBadge && <EditorialBadge>{exp.editorialBadge}</EditorialBadge>}
        {!previewMode && exp.trailerUrl && (
          <PlayOverlay type="button" aria-label="Play trailer" onClick={() => setVideoOpen(true)} />
        )}
      </Hero>

      <Content>
        <BrandMark aria-hidden="true">RealityCheck</BrandMark>
        <Title>{exp.title}</Title>
        <ExperienceRating experienceId={exp.id} summaryOnly />

        {showHeaderEssentials && (
          <HeaderEssentials>
            {priceDisplay && <HeaderPriceLine>{priceDisplay}</HeaderPriceLine>}
            {publishPlatformLine && (
              <HeaderPlatformLine>{publishPlatformLine}</HeaderPlatformLine>
            )}
            <HeaderDeviceIcons experience={exp} />
          </HeaderEssentials>
        )}

        {exp.creatorName && (
          <CreatorRow type="button" aria-label={`By ${exp.creatorName}`}>
            <span>By {exp.creatorName}</span>
            {!previewMode && (
              <>
                <span>·</span>
                <span>Follow</span>
                {exp.creatorExperienceCount != null && (
                  <>
                    <span>·</span>
                    <span>{exp.creatorExperienceCount} experiences</span>
                  </>
                )}
              </>
            )}
          </CreatorRow>
        )}

        {formatPillText && <FormatPill>{formatPillText}</FormatPill>}

        {availability && (
          <StatusRow>
            <StatusDot $color={availability.color} />
            <span>{availability.label}</span>
          </StatusRow>
        )}

        {/* DECISION */}
        {previewText && (
          <>
            <SectionLabel>RealityCheck Preview</SectionLabel>
            <PreviewText>{previewText}</PreviewText>
          </>
        )}

        {quickFacts.length >= 3 && (
          <>
            <SectionLabel>Experience at a Glance</SectionLabel>
            <QuickFactsList>
              {quickFacts.map((fact) => (
                <QuickFactItem key={fact}>{fact}</QuickFactItem>
              ))}
            </QuickFactsList>
          </>
        )}

        {exp.bestFor?.length > 0 && (
          <>
            <SectionLabel>Best for</SectionLabel>
            <BestForEffortRow>
              <BestForChipRow>
                {exp.bestFor.slice(0, 4).map((item) => (
                  <Chip key={item}>{item}</Chip>
                ))}
              </BestForChipRow>
              {exp.effortSignal && <EffortText>{exp.effortSignal}</EffortText>}
            </BestForEffortRow>
          </>
        )}

        {!exp.bestFor?.length && exp.effortSignal && (
          <EffortText style={{ marginBottom: 16 }}>{exp.effortSignal}</EffortText>
        )}

        <RatingsSection>
          <RatingsHeading>Ratings & reviews</RatingsHeading>
          <ExperienceRating experienceId={exp.id} showReviews />
        </RatingsSection>

        <div ref={ctaRef}>
          <PrimaryCTA
            type="button"
            whileHover={previewMode ? undefined : { scale: 1.01 }}
            whileTap={previewMode ? undefined : { scale: 0.98 }}
            onClick={previewMode ? undefined : handlePrimaryCta}
            disabled={previewMode || !canPrimaryCta}
          >
            {ctaLabel}
          </PrimaryCTA>
        </div>
        {previewMode ? (
          <CtaMicrocopy>Preview only — launch is not active until your submission is approved</CtaMicrocopy>
        ) : (
          ctaMicrocopy && <CtaMicrocopy>{ctaMicrocopy}</CtaMicrocopy>
        )}

        {!previewMode && (
          <SecondaryRow>
            {showSaveButton && (
              <SaveButton
                type="button"
                $filled={isSaved}
                onClick={(event) => onSaveClick?.(event.currentTarget)}
              >
                {isSaved ? '★ Saved' : '☆ Save'}
              </SaveButton>
            )}
            <SecondaryButton type="button" onClick={handleShare}>
              ↗ Share
            </SecondaryButton>
            {hasLaunchUrl && (
              <SecondaryButton type="button" onClick={() => setImmersionOpen(true)}>
                📱 Continue in Full Immersion
              </SecondaryButton>
            )}
          </SecondaryRow>
        )}

        {verifiedLine && <VerificationLine>✓ {verifiedLine}</VerificationLine>}

        {/* EXPLORE */}
        {exploreOrder.map((key) => {
          const render = exploreSections[key];
          return render ? <React.Fragment key={key}>{render()}</React.Fragment> : null;
        })}

        {/* DISCOVER — nearby only; creator + similar moved below Full Details */}
        {!previewMode && nearby.length > 0 && (
          <DiscoverSection>
            <DiscoverTitle>Nearby</DiscoverTitle>
            <HorizontalScroll>
              {nearby.map((item) => (
                <ExperienceMiniCard key={item.id} experience={item} onClick={onExperienceClick} />
              ))}
            </HorizontalScroll>
          </DiscoverSection>
        )}

        {hasReferenceContent && (
          <>
            <ReferenceToggle type="button" onClick={() => setReferenceOpen((v) => !v)}>
              <span>Full Details</span>
              <span>{referenceOpen ? '−' : '+'}</span>
            </ReferenceToggle>
            <AnimatePresence>
              {referenceOpen && (
                <ReferencePanel
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  {referenceFields.experience.length +
                    referenceFields.access.length +
                    referenceFields.audience.length >
                    0 && (
                    <ReferenceMainGrid>
                      {[
                        { title: 'The Experience', rows: referenceFields.experience },
                        { title: 'Access', rows: referenceFields.access },
                        { title: 'Audience', rows: referenceFields.audience }
                      ].map(
                        (group) =>
                          group.rows.length > 0 && (
                            <ReferenceGridGroup key={group.title}>
                              <ReferenceGroupTitle>{group.title}</ReferenceGroupTitle>
                              {group.rows.map((row) => (
                                <ReferenceRow key={row.label}>
                                  <strong>{row.label}:</strong> {row.value}
                                </ReferenceRow>
                              ))}
                            </ReferenceGridGroup>
                          )
                      )}
                    </ReferenceMainGrid>
                  )}
                  {referenceFields.time.length > 0 && (
                    <ReferenceGroup>
                      <ReferenceGroupTitle>Time</ReferenceGroupTitle>
                      {referenceFields.time.map((row) => (
                        <ReferenceRow key={row.label}>
                          <strong>{row.label}:</strong> {row.value}
                        </ReferenceRow>
                      ))}
                    </ReferenceGroup>
                  )}
                </ReferencePanel>
              )}
            </AnimatePresence>
          </>
        )}

        {!previewMode && moreFromCreator.length > 0 && (
          <DiscoverSection>
            <DiscoverTitle>More from {exp.creatorName}</DiscoverTitle>
            <HorizontalScroll>
              {moreFromCreator.map((item) => (
                <ExperienceMiniCard key={item.id} experience={item} onClick={onExperienceClick} />
              ))}
            </HorizontalScroll>
          </DiscoverSection>
        )}

        {!previewMode && similar.length > 0 && (
          <DiscoverSection>
            <DiscoverTitle>Similar Experiences</DiscoverTitle>
            <HorizontalScroll>
              {similar.map((item) => (
                <ExperienceMiniCard key={item.id} experience={item} onClick={onExperienceClick} />
              ))}
            </HorizontalScroll>
          </DiscoverSection>
        )}
      </Content>
    </ModalBody>
  );

  if (previewMode) {
    return (
      <PreviewInlineShell>
        <PreviewInlinePanel>{modalInner}</PreviewInlinePanel>
      </PreviewInlineShell>
    );
  }

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          ×
        </CloseButton>

        <Modal
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
        >
          {modalInner}
        </Modal>
      </Overlay>

      {/* Continue in Full Immersion */}
      <AnimatePresence>
        {immersionOpen && (
          <InnerOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setImmersionOpen(false)}
          >
            <InnerModal
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <InnerClose type="button" aria-label="Close" onClick={() => setImmersionOpen(false)}>
                ×
              </InnerClose>
              <InnerTitle>Continue on another device</InnerTitle>
              <InnerSubtitle>Scan to instantly open on the right device</InnerSubtitle>
              {compatibleSurfaces.length > 0 && (
                <VenueLine style={{ fontSize: '0.82rem', marginBottom: 8 }}>
                  {compatibleSurfaces.join(' · ')}
                </VenueLine>
              )}
              <QRBox>
                <QRCodeSVG value={canonicalUrl} size={200} level="M" bgColor="#fff" fgColor="#000" />
              </QRBox>
              <CtaMicrocopy style={{ marginBottom: 12 }}>No searching. No typing. Just scan.</CtaMicrocopy>
              <SecondaryButton type="button" onClick={handleCopyLink} style={{ width: '100%' }}>
                Copy Link
              </SecondaryButton>
            </InnerModal>
          </InnerOverlay>
        )}
      </AnimatePresence>

      {/* Video modal */}
      <AnimatePresence>
        {videoOpen && exp.trailerUrl && (
          <InnerOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideoOpen(false)}
          >
            <InnerModal
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 640 }}
            >
              <InnerClose type="button" aria-label="Close video" onClick={() => setVideoOpen(false)}>
                ×
              </InnerClose>
              <VideoFrame>
                <iframe
                  src={exp.trailerUrl}
                  title={`${exp.title} trailer`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoFrame>
            </InnerModal>
          </InnerOverlay>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}

export default SimpleModal;
